'use client';

import React, { memo, useEffect, useRef, useState } from 'react';
import { Loader } from '@/components/Loader/Loader';
import { saveLoginCredentailsAsCookie } from './utils';
import './LoginModalContent.scss';

// 50 attempts × 100ms = 5 seconds max
const RETRY_INTERVAL_MS = 100;
const MAX_RETRY_ATTEMPTS = 50;

type LoginModalContentProps = {
	onLoginSuccess?: () => void; // Make sure you pass a memoized function here
	onLoadFailed?: () => void;
};

// We don't want to re-render the sso if the parent is re-rendered so we're
// wrapping it in a memo - https://react.dev/reference/react/memo#usage
const SsoWrapper = memo(function SsoWrapper({ id, wrapperRef }: { id: string; wrapperRef: React.RefObject<HTMLElement | null> }) {
	return <cp-client ref={wrapperRef} id={id} className="cp-bz-root cp-bz-site-typography-enabled"></cp-client>;
});

function publish() {
	window?.postal?.publish({ topic: 'sso.login.clicked', data: { contractKey: 'login', model: 'bz4x' }, channel: 'com.toyota.tme.user' });
}

export function LoginModalContent({ onLoginSuccess, onLoadFailed }: LoginModalContentProps) {
	const wrapperRef = useRef<HTMLElement | null>(null);
	const [hasContent, setHasContent] = useState(false);

	useEffect(() => {
		const subscription = window?.postal?.subscribe({
			channel: 'com.toyota.tme.user',
			topic: 'sso.login.ok',
			callback: (data: unknown) => {
				saveLoginCredentailsAsCookie(data);
				onLoginSuccess?.();
			}
		});

		let aborted = false;

		// Retry publish until the cp-client element has content (configuration.js is ready)
		const retryPublish = (attemptsRemaining: number) => {
			if (aborted) return;

			publish();

			const isContentReady = wrapperRef.current && wrapperRef.current.children.length > 0;

			if (isContentReady) {
				setHasContent(true);
			} else if (attemptsRemaining > 0) {
				// Retry as the user is looking at the spinner and waiting for the login modal to load
				setTimeout(retryPublish, RETRY_INTERVAL_MS, attemptsRemaining - 1);
			} else {
				onLoadFailed?.();
			}
		};

		retryPublish(MAX_RETRY_ATTEMPTS);

		return () => {
			aborted = true;
			subscription?.unsubscribe();
		};
		
		// We only want to run this on mount, we have onLoginSuccess here as we need to call back to whatever initialised the modal
		// to tell them the login was a success.  We don't have onLoadFailed in the deps as it's not required and we don't want to 
		// trigger retries if it changes it will just be called on the final retry if the login modal fails to load.  As it comes
		// from zustand it should be stable and not cause re-renders.
	}, [onLoginSuccess]);

	return (
		<>
			{!hasContent && (
				<div className="d-flex justify-center items-center">
					<Loader />
				</div>
			)}
			<div style={{ display: hasContent ? 'block' : 'none' }}>
				<SsoWrapper id="sso-wrapper" wrapperRef={wrapperRef} />
			</div>
		</>
	);
}
